import React, { useMemo } from 'react';
import { SunDim } from 'lucide-react';
import { useDarkMode } from '../../../contexts/DarkModeContext';
import { useFieldLevelSetting } from './hooks/useFieldLevelSetting';
import { TriggerPicker } from './components/TriggerPicker';
import { sectionStyles } from './styles';
import { describeTrigger } from '../../../utils/screenSchedule';
import {
  MAX_LEVEL_PERCENT,
  MIN_LEVEL_PERCENT,
  clampLevels,
  parseNightBrightness,
  serializeNightBrightness,
} from '../../../utils/nightBrightness';
import { formatTemplate, nightBrightnessStrings } from '../../../utils/nightBrightnessStrings';

const LevelRow = ({ id, label, value, min, disabled, onChange, muted }) => (
  <div className="py-3">
    <div className="flex items-center justify-between gap-3">
      <label htmlFor={id} className="text-base font-medium">{label}</label>
      <span className={`text-sm tabular-nums ${muted}`}>{value}%</span>
    </div>
    <input
      id={id}
      type="range"
      min={min}
      max={MAX_LEVEL_PERCENT}
      step={5}
      disabled={disabled}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="mt-2 w-full accent-emerald-600 disabled:cursor-not-allowed"
    />
  </div>
);

/** Auto-dim card: writes only `simpleDashboard.nightBrightness` (all five fields). */
export const AutoDimSettings = ({ onSuccess, onError }) => {
  const { isDarkMode } = useDarkMode();
  const styles = sectionStyles(isDarkMode);
  const strings = useMemo(() => nightBrightnessStrings(navigator.language || 'en'), []);
  const isRtl = (navigator.language || '').toLowerCase().startsWith('ar');

  const [dim, change] = useFieldLevelSetting('nightBrightness', parseNightBrightness, serializeNightBrightness, {
    onSuccess, onError, savedMessage: strings.saved,
  });

  const prayerName = (p) => strings.prayers[p] || p;
  const subtitle = dim.enabled
    ? formatTemplate(strings.subtitleOn, {
      dim: dim.dimLevelPercent,
      from: describeTrigger(dim.dimTrigger, prayerName, strings.minutes),
      until: describeTrigger(dim.restoreTrigger, prayerName, strings.minutes),
    })
    : strings.off;

  const disabled = !dim.enabled;
  const divider = isDarkMode ? 'border-white/10' : 'border-gray-200';
  const muted = isDarkMode ? 'text-gray-400' : 'text-gray-600';

  return (
    <section className={`${styles.container} relative z-0`} dir={isRtl ? 'rtl' : undefined}>
      <div className={styles.header}>
        <SunDim className={styles.headerIcon} />
        <h2 className={styles.headerText}>{strings.title}</h2>
      </div>
      <div className={styles.card}>
        <label className="flex items-center justify-between gap-4 cursor-pointer">
          <div>
            <div className="text-base font-medium">{strings.toggle}</div>
            <div className={`text-sm ${muted}`}>{subtitle}</div>
          </div>
          <input
            type="checkbox"
            role="switch"
            aria-checked={dim.enabled}
            className="h-5 w-5 accent-emerald-600"
            checked={dim.enabled}
            onChange={(e) => change({ enabled: e.target.checked }, true)}
          />
        </label>

        <div className={`mt-4 border-t ${divider} ${disabled ? 'opacity-50' : ''}`}>
          <LevelRow
            id="autodim-dim-level"
            label={strings.dimLevel}
            value={dim.dimLevelPercent}
            min={MIN_LEVEL_PERCENT}
            disabled={disabled}
            muted={muted}
            // Raising the dim level lifts the day level with it (day ≥ dim).
            onChange={(v) => change((prev) => clampLevels(v, prev.dayLevelPercent))}
          />
          <LevelRow
            id="autodim-day-level"
            label={strings.dayLevel}
            value={dim.dayLevelPercent}
            min={Math.max(MIN_LEVEL_PERCENT, dim.dimLevelPercent)}
            disabled={disabled}
            muted={muted}
            onChange={(v) => change((prev) => clampLevels(prev.dimLevelPercent, v))}
          />
        </div>

        <div className={`border-t ${divider}`}>
          <TriggerPicker
            idPrefix="autodim-dim-at"
            label={strings.dimAt}
            trigger={dim.dimTrigger}
            disabled={disabled}
            strings={strings}
            onChange={(dimTrigger) => change({ dimTrigger })}
          />
          <TriggerPicker
            idPrefix="autodim-restore-at"
            label={strings.restoreAt}
            trigger={dim.restoreTrigger}
            disabled={disabled}
            strings={strings}
            onChange={(restoreTrigger) => change({ restoreTrigger })}
          />
        </div>

        <p className={`mt-4 pt-4 border-t ${divider} text-sm ${muted}`}>{strings.footer}</p>
      </div>
    </section>
  );
};
